import { reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string, number, array, date } from 'yup'
import { useReceipts } from './useReceipts'
import { useReceiptDialog } from './useReceiptDialog'
import { useReceiptReject } from './useReceiptReject'
import { useNotifications } from '@/services/notification/useNotifications'
import { useDialog } from '@/services/useDialog'
import { useLogs } from '@/services/useLogs'
import { useTopData } from '@/services/useHome'
import { useLoader } from '../useLoader'
import { useError } from '../useError'
import { expenseTypesAdjust } from '@/services/useExpenseType'
import axios from '@/axios'
import dayjs from 'dayjs'
import * as yup from 'yup'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [
      'expenseTypeId',
      'expenseDate',
      'expenseAmount',
      'haveReceiptImages',
      'expenseContent1',
      'expenseContent2',
      'creditorInfo',
      'denominator',
      'numerator',
      'isDistributed',
      'partialAmount',
      'partialComment',
      'isPartial',
    ],
  },

  { name: '領収書添付', required: true, status: 0, disabled: false },
  { name: '資料添付', required: true, status: 0 },
  { name: '事務局より', required: true, status: 0 },
])

const expenseTypes = reactive(expenseTypesAdjust())

export const useReceiptFormTabs = () => {
  return { tabs }
}

export const amountComparison = () => {
  const { currentReceipt } = useReceipts()
  if (
    currentReceipt.isPartial &&
    currentReceipt.expenseAmount < currentReceipt.partialAmount
  ) {
    return false
  } else {
    return true
  }
}

export const dateComparison = () => {
  const { currentReceipt } = useReceipts()
  if (currentReceipt.expenseDate) {
    const inputDate = new Date(currentReceipt.expenseDate.replace(/-/g, '/'))
    const nowDate = new Date()
    if (inputDate <= nowDate) {
      return true
    } else {
      return false
    }
  }
}

export const distributedValidation = () => {
  const { currentReceipt } = useReceipts()
  if (currentReceipt.numerator !== null && currentReceipt.numerator !== null) {
    if (currentReceipt.numerator < currentReceipt.denominator) {
      return true
    } else {
      return false
    }
  }
}

const schema = object({
  expenseDate: date()
    .required('必須です')
    .typeError('必須です')
    .test(
      'date_validation',
      '支出年月日には現在時刻より過去の年月日を選択してください',
      dateComparison,
    ),
  expenseTypeId: array()
    .min(1)
    .max(1)
    .required('必須です')
    .typeError('必須です'),
  expenseAmount: number()
    .required('必須です')
    .positive('支出額は正の整数で入力してください')
    .integer('支出額は整数で入力してください')
    .typeError('支出額は整数で入力してください'),
  isPartial: yup.boolean(),
  partialAmount: number()
    .nullable()
    .when('isPartial', {
      is: true,
      then: number()
        .positive('充当額は正の整数で入力してください')
        .integer('充当額は整数で入力してください')
        .typeError('充当額は整数で入力してください')
        .required('一部充当金額を入力してください')
        .test(
          'amount_validation',
          '充当額には支出額より小さい金額を入力してください',
          amountComparison,
        ),
    }),
  partialComment: yup
    .string()
    .nullable()
    .when('isPartial', {
      is: true,
      then: yup.string().required('一部充当内容を入力してください'),
    }),
  isDistributed: yup.boolean(),
  numerator: number()
    .nullable()
    .when('isDistributed', {
      is: true,
      then: number()
        .nullable()
        .typeError('按分を入力してください')
        .required('按分を入力してください')
        .test(
          'isDistributed_validation',
          '按分率は100%未満になるように入力してください',
          distributedValidation,
        ),
    }),

  denominator: yup.number().nullable(),
  haveReceiptImages: yup.boolean(),
  expenseContent1: string()
    .nullable()
    .required('必須です'),
  expenseContent2: string().nullable(),
  creditorInfo: string().nullable(),
  files: array(),
  additionalResources: array(),
})

export const useReceiptForm = () => {
  const { currentReceipt, getReceiptsAfterResponse } = useReceipts()
  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const { receiptDialog } = useReceiptDialog()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()
  watch(receiptDialog, () => {
    if (receiptDialog.isPreview) {
      currentReceipt.expenseDate = expenseDate
        ? dayjs(expenseDate.value).format('YYYY-MM-DD HH:mm:ss')
        : currentReceipt.expenseDate
      currentReceipt.expenseTypeId = expenseTypeId
        ? [...expenseTypeId.value]
        : currentReceipt.expenseTypeId
      currentReceipt.expenseAmount = expenseAmount
        ? expenseAmount.value
        : currentReceipt.expenseAmount
      currentReceipt.haveReceiptImages = haveReceiptImages
        ? haveReceiptImages.value
        : currentReceipt.haveReceiptImages
      currentReceipt.expenseContent1 = expenseContent1
        ? expenseContent1.value
        : currentReceipt.expenseContent1
      currentReceipt.expenseContent2 = expenseContent2
        ? expenseContent2.value
        : currentReceipt.expenseContent2
      currentReceipt.creditorInfo = creditorInfo
        ? creditorInfo.value
        : currentReceipt.creditorInfo
      currentReceipt.denominator = denominator
        ? denominator.value
        : currentReceipt.denominator
      currentReceipt.numerator = numerator
        ? numerator.value
        : currentReceipt.numerator
      currentReceipt.isDistributed = isDistributed
        ? isDistributed.value
        : currentReceipt.isDistributed
      currentReceipt.partialAmount = partialAmount
        ? partialAmount.value
        : currentReceipt.partialAmount
      currentReceipt.partialComment = partialComment
        ? partialComment.value
        : currentReceipt.partialComment
      currentReceipt.isPartial = isPartial
        ? isPartial.value
        : currentReceipt.isPartial
    }
  })

  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    meta.valid
      ? (receiptDialog.isPreviewEnabled = true)
      : (receiptDialog.isPreviewEnabled = false)
  })

  const { value: expenseDate, errorMessage: expenseDateError } = useField(
    'expenseDate',
    undefined,
    {
      initialValue: currentReceipt.expenseDate
        ? dayjs(currentReceipt.expenseDate).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    },
  )

  const { value: expenseTypeId, errorMessage: expenseTypeIdError } = useField(
    'expenseTypeId',
    undefined,
    {
      initialValue: currentReceipt.expenseTypeId,
    },
  )

  const { value: expenseAmount, errorMessage: expenseAmountError } = useField(
    'expenseAmount',
    undefined,
    {
      initialValue: currentReceipt.expenseAmount,
    },
  )
  const { value: denominator, errorMessage: denominatorError } = useField(
    'denominator',
    undefined,
    {
      initialValue: currentReceipt.denominator,
    },
  )
  const { value: numerator, errorMessage: numeratorError } = useField(
    'numerator',
    undefined,
    {
      initialValue: currentReceipt.numerator,
    },
  )

  const {
    value: haveReceiptImages,
    errorMessage: haveReceiptImagesError,
  } = useField('haveReceiptImages', undefined, {
    initialValue: currentReceipt.haveReceiptImages ?? false,
  })

  const {
    value: expenseContent1,
    errorMessage: expenseContent1Error,
  } = useField('expenseContent1', undefined, {
    initialValue: currentReceipt.expenseContent1,
  })

  const {
    value: expenseContent2,
    errorMessage: expenseContent2Error,
  } = useField('expenseContent2', undefined, {
    initialValue: currentReceipt.expenseContent2,
  })
  const { value: creditorInfo, errorMessage: creditorInfoError } = useField(
    'creditorInfo',
    undefined,
    {
      initialValue: currentReceipt.creditorInfo,
    },
  )
  const { value: isDistributed, errorMessage: isDistributedError } = useField(
    'isDistributed',
    undefined,
    {
      initialValue: currentReceipt.isDistributed ?? false,
    },
  )

  const { value: partialAmount, errorMessage: partialAmountError } = useField(
    'partialAmount',
    undefined,
    {
      initialValue: currentReceipt.partialAmount,
    },
  )
  const { value: partialComment, errorMessage: partialCommentError } = useField(
    'partialComment',
    undefined,
    {
      initialValue: currentReceipt.partialComment,
    },
  )

  const { value: isPartial, errorMessage: isPartialError } = useField(
    'isPartial',
    undefined,
    {
      initialValue: currentReceipt.isPartial ?? false,
    },
  )

  const { value: files, errorMessage: filesError } = useField(
    'files',
    undefined,
    { initialValue: currentReceipt.files },
  )
  const { value: sendBack, errorMessage: sendBackError } = useField(
    'sendBack',
    undefined,
    {
      initialValue: currentReceipt.sendBack,
    },
  )

  const {
    value: additionalResources,
    errorMessage: additionalResourcesError,
  } = useField('additionalResources', undefined, {
    initialValue: currentReceipt.additionalResources,
  })

  const handleImages = files => {
    if (files.length > 0) {
      // tabs[1].required = true
      // tabs[1].addFilesBtn = true
      tabs[1].status = 1
    } else {
      // tabs[1].required = false
      // tabs[1].addFilesBtn = false
      tabs[1].status = 0
    }
  }

  const handleFiles = files => {
    if (files.length > 0) {
      tabs[2].status = 1
    } else {
      tabs[2].status = 0
    }
  }

  const getSentBackItemMessage = attr => {
    const sentBackList = []
    if (
      currentReceipt.option.viewType === 0 ||
      currentReceipt.option.viewType === 1 ||
      currentReceipt.sentBackItems.length == 0
    ) {
      return ''
    }
    currentReceipt.sentBackItems.forEach(item => {
      if (!item || item.length == 0) return ''
      const backItem = item.items.find(item => item.target == attr)
      if (backItem !== undefined) {
        sentBackList.push(backItem)
      }
    })
    if (sentBackList.length > 0) {
      sentBackList
      return sentBackList
    }
    return ''
  }

  // 領収書ダイアログ「入力を確認する」ボタンを押したときにバリデーションを更新する。エラーがなければプレビュー画面へ
  const receiptSubmit = handleSubmit(isShown => {
    receiptDialog.isPreview = isShown
  })

  watch(expenseDate, () => {
    currentReceipt.expenseDate = dayjs(expenseDate.value).format(
      'YYYY-MM-DD HH:mm:ss',
    )
    setStatus(0)
  })
  watch(expenseAmount, () => {
    currentReceipt.expenseAmount = expenseAmount.value
    setStatus(0)
  })
  watch(haveReceiptImages, () => {
    currentReceipt.haveReceiptImages = haveReceiptImages.value
    if (currentReceipt.haveReceiptImages) {
      tabs[1].disabled = true
    } else {
      tabs[1].disabled = false
    }
  })
  watch(expenseContent1, () => {
    currentReceipt.expenseContent1 = expenseContent1.value
    setStatus(0)
  })
  watch(expenseContent2, () => {
    currentReceipt.expenseContent2 = expenseContent2.value
  })
  watch(creditorInfo, () => {
    currentReceipt.creditorInfo = creditorInfo.value
  })
  watch(denominator, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (denominator.value == '') {
      denominator.value = null
      currentReceipt.denominator = null
    } else {
      currentReceipt.denominator = denominator.value
    }
    setStatus(0)
  })
  watch(numerator, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (numerator.value == '') {
      numerator.value = null
      currentReceipt.numerator = null
    } else {
      currentReceipt.numerator = numerator.value
    }
    setStatus(0)
  })
  watch(expenseTypeId, () => {
    currentReceipt.expenseTypeId = expenseTypeId.value
    setStatus(0)
  })
  watch(isDistributed, () => {
    currentReceipt.isDistributed = isDistributed.value
    setStatus(0)
  })
  watch(partialAmount, () => {
    // v-modelがからの時に''から文字が入ってしまいnumber()のバリデーションでエラーを出してしまうため、nullを入れる
    if (partialAmount.value == '') {
      partialAmount.value = null
      currentReceipt.partialAmount = null
    } else {
      currentReceipt.partialAmount = partialAmount.value
    }
    setStatus(0)
  })
  watch(partialComment, () => {
    currentReceipt.partialComment = partialComment.value
    setStatus(0)
  })
  watch(isPartial, () => {
    currentReceipt.isPartial = isPartial.value
    setStatus(0)
  })

  // validateのタイミングでタブのstatusを更新する
  watch(expenseDateError, () => {
    setStatus(0)
  })
  watch(expenseAmountError, () => {
    setStatus(0)
  })
  watch(expenseContent1Error, () => {
    setStatus(0)
  })
  watch(expenseTypeIdError, () => {
    setStatus(0)
  })
  watch(denominatorError, () => {
    setStatus(0)
  })
  watch(numeratorError, () => {
    setStatus(0)
  })

  const setStatus = index => {
    const err = error(index)
    const inp = input(index)
    if (err && inp) {
      tabs[index].status = 1
    } else if (err && !inp) {
      tabs[index].status = 0
    } else if (!err) {
      tabs[index].status = -1
    }
  }

  // エラーがある時falseを返す
  const error = index => {
    let err = false

    if (index === 0) {
      err =
        typeof expenseDateError.value === 'undefined' &&
        typeof expenseAmountError.value === 'undefined' &&
        typeof expenseContent1Error.value === 'undefined' &&
        typeof expenseTypeIdError.value === 'undefined' &&
        typeof denominatorError.value === 'undefined' &&
        typeof numeratorError.value === 'undefined'
    }
    return err
  }

  const input = index => {
    let inp = false
    if (index === 0) {
      inp = expenseDate.value && expenseAmount.value && expenseContent1.value
    }
    return inp
  }

  /**
   * 諸経費を新規作成/更新する
   *
   * @param {number|null} id approve_history.document_id 新規作成はnull指定
   * @param {string} type receipt: 一時保存, receipt-apply: 適用
   */
  const submitReceipt = async (id, type) => {
    try {
      toggleShowLoader(true)
      if (!(type === 'receipt' || type === 'receipt-apply')) {
        throw new Error('invalid type: ' + type)
      }
      // fileの送信を行うためformDataオブジェクトを使用する
      const formdata = new FormData()

      // 保存タイプのフラグのフォームデータを追加
      formdata.append('is_temporary', type === 'receipt')

      // 経費項目のフォームデータを追加
      if (currentReceipt.expenseTypeId) {
        formdata.append('expense_type_id', currentReceipt.expenseTypeId[0]?.id)
      }

      // 支出日のフォームデータを追加
      if (currentReceipt.expenseDate) {
        formdata.append('expense_date', currentReceipt.expenseDate)
      }

      // 支出額のフォームデータを追加
      if (currentReceipt.expenseAmount) {
        formdata.append('expense_amount', currentReceipt.expenseAmount)
      }

      // 支払い証明書フラグのフォームデータを追加(入力時のチェックボックスのboolとhaveReceiptImagesのboolの判定が逆のためhaveReceiptImagesのboolを反転させる)
      formdata.append('have_receipt_images', !currentReceipt.haveReceiptImages)

      // 支出内容のフォームデータを追加
      if (currentReceipt.expenseContent1) {
        formdata.append('expense_content1', currentReceipt.expenseContent1)
      }

      // 補記のフォームデータを追加
      if (currentReceipt.expenseContent2) {
        formdata.append('expense_content2', currentReceipt.expenseContent2)
      }

      // 債権者住所、氏名のフォームデータを追加
      if (currentReceipt.haveReceiptImages && currentReceipt.creditorInfo) {
        formdata.append('creditor_info', currentReceipt.creditorInfo)
      }

      // 一部充当フラグのフォームデータを追加
      if (currentReceipt.isPartial) {
        formdata.append('is_partial', currentReceipt.isPartial)
      }

      // 一部充当額のフォームデータを追加
      if (currentReceipt.partialAmount && currentReceipt.isPartial === true) {
        formdata.append('partial_amount', currentReceipt.partialAmount)
      }

      // 一部充当内容のフォームデータを追加
      if (currentReceipt.partialComment && currentReceipt.isPartial === true) {
        formdata.append('partial_comment', currentReceipt.partialComment)
      }

      // 按分フラグのフォームデータを追加
      formdata.append('is_distributed', currentReceipt.isDistributed)

      // 分子のフォームデータを追加
      if (currentReceipt.numerator && currentReceipt.isDistributed === true)
        formdata.append('numerator', currentReceipt.numerator)

      // 分母のフォームデータを追加
      if (currentReceipt.denominator && currentReceipt.isDistributed === true)
        formdata.append('denominator', currentReceipt.denominator)

      // 添付資料のフォームデータを追加
      currentReceipt.additionalResources.forEach(file => {
        if (file?.display) {
          formdata.append('keep_file_names[]', file.display)
        } else {
          formdata.append('additional_resources[]', file)
        }
      })

      // 画像のフォームデータを追加
      for (let i = 0; i < currentReceipt.files.length; i++) {
        Object.keys(currentReceipt.files[i]).forEach(key => {
          if (currentReceipt.files[i][key]) {
            const keyName = 'images[' + i + '][' + key + ']'
            formdata.append(keyName, currentReceipt.files[i][key])
          }
        })
      }
      // postだとだとマルチパート形式のファイル送信がうまくいかないためputをオーバーライド
      let config = {
        headers: {
          'content-type': 'multipart/form-data',
          'X-HTTP-Method-Override': 'PUT',
        },
      }

      const isPost = id === null
      const response = isPost
        ? await axios.post(`/receipts`, formdata, config)
        : await axios.post(`/receipts/${id}`, formdata, config)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const cancelRequest = async id => {
    try {
      toggleShowLoader(true)
      const response = await axios.post(`/receipts/cancel/${id}`)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const approveRequest = async (id, userRank) => {
    try {
      toggleShowLoader(true)
      const { rejectedAttrs, toSnake } = useReceiptReject()
      let data = {}
      // 会派事務局の時にアップデートを行う
      if (userRank === 2) {
        // fileの送信を行うためformDataオブジェクトを使用する
        const formdata = new FormData()

        // 保存タイプのフラグのフォームデータを追加
        formdata.append('is_temporary', false)

        // 経費項目のフォームデータを追加
        if (currentReceipt.expenseTypeId) {
          formdata.append(
            'expense_type_id',
            currentReceipt.expenseTypeId[0]?.id,
          )
        }

        // 支出日のフォームデータを追加
        if (currentReceipt.expenseDate) {
          formdata.append('expense_date', currentReceipt.expenseDate)
        }

        // 支出額のフォームデータを追加
        if (currentReceipt.expenseAmount) {
          formdata.append('expense_amount', currentReceipt.expenseAmount)
        }

        // 支払い証明書フラグのフォームデータを追加(入力時のチェックボックスのboolとhaveReceiptImagesのboolの判定が逆のためhaveReceiptImagesのboolを反転させる)
        formdata.append(
          'have_receipt_images',
          !currentReceipt.haveReceiptImages,
        )

        // 支出内容のフォームデータを追加
        if (currentReceipt.expenseContent1) {
          formdata.append('expense_content1', currentReceipt.expenseContent1)
        }

        // 補記のフォームデータを追加
        if (currentReceipt.expenseContent2) {
          formdata.append('expense_content2', currentReceipt.expenseContent2)
        }

        // 債権者住所、氏名のフォームデータを追加
        if (currentReceipt.haveReceiptImages && currentReceipt.creditorInfo) {
          formdata.append('creditor_info', currentReceipt.creditorInfo)
        }

        // 一部充当フラグのフォームデータを追加
        if (currentReceipt.isPartial) {
          formdata.append('is_partial', currentReceipt.isPartial)
        }

        // 一部充当額のフォームデータを追加
        if (currentReceipt.partialAmount && currentReceipt.isPartial === true) {
          formdata.append('partial_amount', currentReceipt.partialAmount)
        }

        // 一部充当内容のフォームデータを追加
        if (
          currentReceipt.partialComment &&
          currentReceipt.isPartial === true
        ) {
          formdata.append('partial_comment', currentReceipt.partialComment)
        }

        // 按分フラグのフォームデータを追加
        formdata.append('is_distributed', currentReceipt.isDistributed)

        // 分子のフォームデータを追加
        if (currentReceipt.numerator && currentReceipt.isDistributed === true)
          formdata.append('numerator', currentReceipt.numerator)

        // 分母のフォームデータを追加
        if (currentReceipt.denominator && currentReceipt.isDistributed === true)
          formdata.append('denominator', currentReceipt.denominator)

        // 添付資料のフォームデータを追加
        currentReceipt.additionalResources.forEach(file => {
          if (file?.display) {
            formdata.append('keep_file_names[]', file.display)
          } else {
            formdata.append('additional_resources[]', file)
          }
        })

        // 画像のフォームデータを追加
        if (currentReceipt.files.length !== 0) {
          for (let i = 0; i < currentReceipt.files.length; i++) {
            Object.keys(currentReceipt.files[i]).forEach(key => {
              if (currentReceipt.files[i][key]) {
                const keyName = 'images[' + i + '][' + key + ']'
                formdata.append(keyName, currentReceipt.files[i][key])
              }
            })
          }
        } else {
          for (let i = 0; i < currentReceipt.images.length; i++) {
            Object.keys(currentReceipt.images[i]).forEach(key => {
              if (currentReceipt.images[i][key]) {
                const keyName = 'images[' + i + '][' + key + ']'
                formdata.append(keyName, currentReceipt.images[i][key])
              }
            })
          }
        }
        let config = {
          headers: {
            'content-type': 'multipart/form-data',
            'X-HTTP-Method-Override': 'PUT',
          },
        }
        await axios.post(`/receipts/${id}`, formdata, config)
      }
      if (rejectedAttrs.length !== 0) {
        toSnake()
        data = {
          sent_back_items: rejectedAttrs,
        }
      }

      const response = await axios.post(`/receipts/approve/${id}`, data)
      if (route.name === 'Receipts') {
        await getReceiptsAfterResponse(response)
      } else if (route.name !== 'Receipts') {
        const { closeDialog } = useDialog()
        closeDialog()
        if (route.name === 'Top') {
          await getTopDataAfterResponse(response)
        } else if (route.name === 'Logs') {
          await getTimelineAfterResponse(response)
        } else if (route.name === 'Notifications') {
          await getNotificationsAfterResponse(response)
        }
      }
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 按分計算し、計算した結果にカンマ区切りをつけた文字列を返す
   *
   * @returns {string}
   */
  const prorateCalc = () => {
    const amount = currentReceipt.expenseAmount
    const denominator = currentReceipt.denominator
    const numerator = currentReceipt.numerator
    const amountStr = amount.toLocaleString()
    if (denominator == 0 || numerator == 0) {
      return `${amountStr}円`
    }
    const proratedAmount = Math.floor((numerator / denominator) * amount)
    const proratedStr = proratedAmount.toLocaleString()
    return `${proratedStr}円 [${amountStr}円 按分 ${numerator}/${denominator}]`
  }

  return {
    expenseTypes,
    handleImages,
    handleFiles,
    getSentBackItemMessage,
    submitReceipt,
    cancelRequest,
    approveRequest,
    prorateCalc,
    amountComparison,
    handleSubmit,
    receiptSubmit,
    meta,
    expenseDate,
    expenseDateError,
    expenseTypeId,
    expenseTypeIdError,
    expenseAmount,
    expenseAmountError,
    denominator,
    denominatorError,
    numerator,
    numeratorError,
    haveReceiptImages,
    haveReceiptImagesError,
    expenseContent1,
    expenseContent1Error,
    expenseContent2,
    expenseContent2Error,
    creditorInfo,
    creditorInfoError,
    partialAmount,
    partialAmountError,
    isDistributed,
    isDistributedError,
    partialComment,
    partialCommentError,
    isPartial,
    isPartialError,
    files,
    filesError,
    additionalResources,
    additionalResourcesError,
    sendBack,
    sendBackError,
  }
}
